import { useState, useEffect } from 'react'
import { useAuth } from '@/context/AuthContext'
import { resendVerificationEmail } from '@/services/emailVerificationService'
import { ROLES } from '@/config/roles'
import { toast } from 'sonner'
import { FaExclamationCircle, FaTimes } from 'react-icons/fa'
import './VerificationReminderBanner.css'

const VerificationReminderBanner = () => {
  const { currentUser, emailVerified, fetchUserData, userRole } = useAuth()
  const [resending, setResending] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  // Check if banner was previously dismissed
  useEffect(() => {
    const wasDismissed = localStorage.getItem('verificationBannerDismissed')
    if (wasDismissed === 'true') {
      setDismissed(true)
    }
  }, [])

  // Clear dismissal when email is verified
  useEffect(() => {
    if (emailVerified) {
      localStorage.removeItem('verificationBannerDismissed')
      setDismissed(false)
    }
  }, [emailVerified])

  // Only show for PARENT and SCHOOL_ADMIN roles
  const shouldShowBanner =
    currentUser &&
    !emailVerified &&
    !dismissed &&
    (userRole === ROLES.PARENT || userRole === ROLES.SCHOOL_ADMIN)

  if (!shouldShowBanner) {
    return null
  }

  const handleResend = async () => {
    if (!currentUser?.email) {
      toast.error('Unable to resend verification code')
      return
    }

    setResending(true)
    try {
      await resendVerificationEmail(currentUser.uid, currentUser.email)
      toast.success('Verification code sent! Please check your inbox.')
      if (fetchUserData) {
        await fetchUserData(currentUser.uid)
      }
    } catch (error) {
      console.error('Resend error:', error)
      // Check if account is already verified
      if (error.message && error.message.includes('already been verified')) {
        toast.info('This account has already been verified.')
        // Refresh user data to update the banner
        if (fetchUserData) {
          await fetchUserData(currentUser.uid)
        }
      } else {
        toast.error(
          error.message || 'Failed to resend email. Please try again later.'
        )
      }
    } finally {
      setResending(false)
    }
  }

  const handleDismiss = () => {
    setDismissed(true)
    // Store dismissal in localStorage to persist across sessions
    localStorage.setItem('verificationBannerDismissed', 'true')
  }

  return (
    <div className='verification-reminder-banner'>
      <div className='verification-reminder-header'>
        <button
          onClick={handleDismiss}
          className='verification-reminder-dismiss-btn'
          aria-label='Dismiss banner'
        >
          <FaTimes />
        </button>
      </div>
      <div className='verification-reminder-content'>
        <FaExclamationCircle className='verification-reminder-icon' />
        <div className='verification-reminder-text'>
          <strong>Please verify your email address</strong>
          <span>
            We sent a 6-digit verification code to {currentUser.email}. Enter
            it to verify your account.
          </span>
        </div>
      </div>
      <div className='verification-reminder-footer'>
        <button
          onClick={handleResend}
          disabled={resending}
          className='verification-reminder-resend-btn'
        >
          {resending ? 'Sending...' : 'Resend Code'}
        </button>
      </div>
    </div>
  )
}

export default VerificationReminderBanner
